import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LoadingSpinner from '@/components/LoadingSpinner';
import { 
  ChevronLeft,
  Eye,
  Users,
  TrendingUp,
  MapPin,
  Calendar,
  Clock,
  Star,
  MessageSquare,
  Phone,
  Globe,
  BarChart3,
  Activity,
  Target,
  Award,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useOperatorApi } from '@/hooks/useRoleBasedApi';

interface AnalyticsData {
  overview: {
    totalViews: number;
    uniqueVisitors: number;
    averageTimeOnPage: number;
    bounceRate: number;
    conversionRate: number;
  };
  engagement: {
    profileViews: number;
    contactClicks: number;
    directionRequests: number;
    bookingInquiries: number;
    reviewsSubmitted: number;
  };
  demographics: {
    topCountries: Array<{ country: string; visitors: number; percentage: number }>;
    topCities: Array<{ city: string; visitors: number; percentage: number }>;
    devices: Array<{ device: string; visitors: number; percentage: number }>;
  };
  trends: Array<{
    date: string;
    views: number;
    visitors: number;
    interactions: number;
  }>;
  customerInteractions: Array<{
    id: string;
    type: 'view' | 'contact' | 'direction' | 'booking' | 'review';
    customerInfo: string;
    timestamp: string;
    location?: string;
  }>;
}

const OperatorAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const operatorApi = useOperatorApi();
  
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // In development, use mock data immediately
      if (import.meta.env.DEV) {
        // Simulate a small delay to show loading state
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const mockData: AnalyticsData = {
          overview: {
            totalViews: 1247,
            uniqueVisitors: 892,
            averageTimeOnPage: 145,
            bounceRate: 23.4,
            conversionRate: 8.7
          },
          engagement: {
            profileViews: 1247,
            contactClicks: 156,
            directionRequests: 89,
            bookingInquiries: 34,
            reviewsSubmitted: 12
          },
          demographics: {
            topCountries: [
              { country: 'Ireland', visitors: 756, percentage: 84.8 },
              { country: 'United Kingdom', visitors: 89, percentage: 10.0 },
              { country: 'United States', visitors: 47, percentage: 5.2 }
            ],
            topCities: [
              { city: 'Dublin', visitors: 423, percentage: 47.4 },
              { city: 'Cork', visitors: 156, percentage: 17.5 },
              { city: 'Galway', visitors: 134, percentage: 15.0 },
              { city: 'Limerick', visitors: 89, percentage: 10.0 },
              { city: 'Waterford', visitors: 90, percentage: 10.1 }
            ],
            devices: [
              { device: 'Mobile', visitors: 623, percentage: 69.8 },
              { device: 'Desktop', visitors: 201, percentage: 22.5 },
              { device: 'Tablet', visitors: 68, percentage: 7.7 }
            ]
          },
          trends: [
            { date: '2024-01-01', views: 45, visitors: 32, interactions: 12 },
            { date: '2024-01-02', views: 52, visitors: 38, interactions: 15 },
            { date: '2024-01-03', views: 48, visitors: 35, interactions: 13 },
            { date: '2024-01-04', views: 61, visitors: 44, interactions: 18 },
            { date: '2024-01-05', views: 67, visitors: 49, interactions: 22 },
            { date: '2024-01-06', views: 73, visitors: 55, interactions: 25 },
            { date: '2024-01-07', views: 69, visitors: 52, interactions: 23 }
          ],
          customerInteractions: [
            {
              id: '1',
              type: 'contact',
              customerInfo: 'John D. from Dublin',
              timestamp: '2024-01-07T14:30:00Z',
              location: 'Dublin'
            },
            {
              id: '2',
              type: 'direction',
              customerInfo: 'Sarah M. from Cork',
              timestamp: '2024-01-07T13:15:00Z',
              location: 'Cork'
            },
            {
              id: '3',
              type: 'booking',
              customerInfo: 'Mike R. from Galway',
              timestamp: '2024-01-07T12:45:00Z',
              location: 'Galway'
            },
            {
              id: '4',
              type: 'review',
              customerInfo: 'Lisa K. from Limerick',
              timestamp: '2024-01-07T11:20:00Z',
              location: 'Limerick'
            }
          ]
        };
        
        setAnalyticsData(mockData);
        setLoading(false);
        return;
      }

      // Real API call (only if user is authenticated)
      if (user) {
        const data = await operatorApi.get(`/analytics?range=${timeRange}`);
        setAnalyticsData(data);
      } else {
        setError('Authentication required to view analytics');
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      
      // Report specific backend issues
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          console.error('BACKEND ISSUE: Network error - backend may be down or CORS not configured');
        } else if (error.message.includes('401')) {
          console.error('BACKEND ISSUE: Authentication error - user session may be invalid');
        } else if (error.message.includes('404')) {
          console.error('BACKEND ISSUE: API endpoint not found - check if /analytics endpoint exists');
        } else if (error.message.includes('500')) {
          console.error('BACKEND ISSUE: Server error - backend may have crashed');
        } else {
          console.error('BACKEND ISSUE:', error.message);
        }
      }
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to safely render country data
  const renderCountry = (country: any): string => {
    if (typeof country === 'string') {
      return country;
    } else if (country && typeof country === 'object') {
      return country.name || country.code || 'Unknown';
    } else {
      return 'Unknown';
    }
  };

  // Helper function to safely render city data
  const renderCity = (city: any): string => {
    if (typeof city === 'string') {
      return city;
    } else if (city && typeof city === 'object') {
      return city.name || city.code || 'Unknown';
    } else {
      return 'Unknown';
    }
  };

  const getInteractionIcon = (type: string) => {
    switch (type) {
      case 'view': return <Eye className="w-4 h-4" />;
      case 'contact': return <Phone className="w-4 h-4" />;
      case 'direction': return <MapPin className="w-4 h-4" />;
      case 'booking': return <Calendar className="w-4 h-4" />;
      case 'review': return <Star className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getInteractionColor = (type: string) => {
    switch (type) {
      case 'view': return 'bg-blue-100 text-blue-700';
      case 'contact': return 'bg-green-100 text-green-700';
      case 'direction': return 'bg-purple-100 text-purple-700';
      case 'booking': return 'bg-orange-100 text-orange-700';
      case 'review': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading analytics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Failed to load analytics</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={fetchAnalyticsData}>Try Again</Button>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No analytics data available</h3>
          <p className="text-gray-500">Analytics will appear here once your listing receives traffic.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analytics | SaunaDip</title>
        <meta name="description" content="Track how people interact with your sauna listing" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Analytics</h1>
                <p className="text-sm text-gray-500">Measure how your published project is performing</p>
              </div>
            </div>
            
            {/* Time Range Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Last</span>
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
                className="text-sm border border-gray-300 rounded-md px-2 py-1"
              >
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
                <option value="90d">90 days</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4">
          <div className="max-w-7xl mx-auto">
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Views</p>
                      <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.totalViews.toLocaleString()}</p>
                    </div>
                    <Eye className="w-8 h-8 text-sauna-blue" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Unique Visitors</p>
                      <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.uniqueVisitors.toLocaleString()}</p>
                    </div>
                    <Users className="w-8 h-8 text-sauna-green" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg. Time</p>
                      <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.averageTimeOnPage}s</p>
                    </div>
                    <Clock className="w-8 h-8 text-sauna-orange" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Bounce Rate</p>
                      <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.bounceRate}%</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Conversion</p>
                      <p className="text-2xl font-bold text-gray-900">{analyticsData.overview.conversionRate}%</p>
                    </div>
                    <Target className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content Tabs */}
            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="engagement">Engagement</TabsTrigger>
                <TabsTrigger value="demographics">Demographics</TabsTrigger>
                <TabsTrigger value="interactions">Recent Activity</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Engagement Metrics */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Engagement Metrics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Profile Views</span>
                          <span className="font-semibold">{analyticsData.engagement.profileViews}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Contact Clicks</span>
                          <span className="font-semibold">{analyticsData.engagement.contactClicks}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Direction Requests</span>
                          <span className="font-semibold">{analyticsData.engagement.directionRequests}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Booking Inquiries</span>
                          <span className="font-semibold">{analyticsData.engagement.bookingInquiries}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Reviews Submitted</span>
                          <span className="font-semibold">{analyticsData.engagement.reviewsSubmitted}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Performance Chart Placeholder */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Performance Trends</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-md">
                        <div className="text-center">
                          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">Chart visualization coming soon</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Engagement Tab */}
              <TabsContent value="engagement" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(analyticsData.engagement).map(([key, value]) => (
                    <Card key={key}>
                      <CardContent className="p-6">
                        <div className="text-center">
                          <div className="text-3xl font-bold text-sauna-orange mb-2">{value}</div>
                          <div className="text-sm text-gray-600 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* Demographics Tab */}
              <TabsContent value="demographics" className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Top Countries */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Countries</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.demographics.topCountries.map((country, index) => (
                          <div key={country.country} className="flex justify-between items-center">
                            <span className="text-sm">{renderCountry(country.country)}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-sauna-blue h-2 rounded-full" 
                                  style={{ width: `${country.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">{country.visitors}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Cities */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Top Cities</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.demographics.topCities.map((city, index) => (
                          <div key={city.city} className="flex justify-between items-center">
                            <span className="text-sm">{renderCity(city.city)}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-sauna-green h-2 rounded-full" 
                                  style={{ width: `${city.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">{city.visitors}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Devices */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Device Types</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.demographics.devices.map((device, index) => (
                          <div key={device.device} className="flex justify-between items-center">
                            <span className="text-sm">{device.device}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-sauna-orange h-2 rounded-full" 
                                  style={{ width: `${device.percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm font-medium">{device.visitors}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Recent Activity Tab */}
              <TabsContent value="interactions" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Customer Interactions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {analyticsData.customerInteractions.map((interaction) => (
                        <div key={interaction.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`p-2 rounded-full ${getInteractionColor(interaction.type)}`}>
                            {getInteractionIcon(interaction.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{interaction.customerInfo}</span>
                              <span className="text-sm text-gray-500">
                                {new Date(interaction.timestamp).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)}
                              </Badge>
                              {interaction.location && (
                                <span className="text-sm text-gray-500">{interaction.location}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperatorAnalytics; 